<template>
  <div id="app" class="m-0 p-0">
    <b-navbar toggleable variant="dark" type="dark">
      <b-navbar-brand href="#">
        MS Sample Notice System
      </b-navbar-brand>

      <b-navbar-toggle target="navbar-toggle-collapse">
        <template v-slot:default="{ expanded }">
          <b-icon v-if="expanded" icon="chevron-bar-up"></b-icon>
          <b-icon v-else icon="chevron-bar-down"></b-icon>
        </template>
      </b-navbar-toggle>

      <b-collapse id="navbar-toggle-collapse" is-nav>
        <b-navbar-nav class="ml-auto">
          <b-nav-item v-if="canInstall" @click="installer()">Install App</b-nav-item>
          <b-nav-item v-if="isIOS" v-b-modal.ios-install-modal>Install App</b-nav-item>
          <b-nav-item v-b-modal.settings-modal>
            <b-icon icon="gear" class="mr-1"></b-icon>Settings
          </b-nav-item>
          <b-nav-item href="#">About</b-nav-item>
        </b-navbar-nav>
      </b-collapse>
      <b-modal id="ios-install-modal" title="Install on iPhone/iPad" ok-only ok-variant="dark" centered>
        <p>To install this app on your iOS device:</p>
        <ol class="text-left">
          <li>Tap the <strong>Share</strong> button <span style="font-size: 1.2em;">&#x1F4E4;</span> at the bottom of Safari</li>
          <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
          <li>Tap <strong>"Add"</strong> in the top right</li>
        </ol>
        <p class="mt-3 text-muted small">Note: Push notifications are limited on iOS. Check back regularly for new results.</p>
      </b-modal>
    </b-navbar>
    <b-alert
      v-model="showInstallBanner"
      variant="light"
      dismissible
      class="mx-2 mt-2 mb-0 py-2 border"
    >
      <b-button
        v-if="canInstall"
        variant="primary"
        size="sm"
        @click="installer()"
      >Install</b-button>
      <b-button
        v-else-if="isIOS"
        variant="primary"
        size="sm"
        v-b-modal.ios-install-modal
      >Install</b-button>
      <span class="ml-2 small">Add to home screen for quick access</span>
    </b-alert>
    <div class="container-fluid px-2 px-md-4 py-3">
      <!-- PWS Lookup Card -->
      <b-card
        class="mx-auto mb-3 shadow-sm"
        style="max-width: 28rem;"
        body-class="p-3"
      >
        <template #header>
          <div class="d-flex align-items-center">
            <b-icon icon="droplet-fill" class="text-primary mr-2"></b-icon>
            <strong>Public Water System</strong>
          </div>
        </template>
        <b-form @submit.stop.prevent="onSubmit">
          <b-form-group class="mb-2">
            <b-input-group>
              <b-input-group-prepend is-text class="small">
                PWS ID
              </b-input-group-prepend>
              <b-form-input
                id="pws-id"
                v-model="pws_id"
                type="number"
                :formatter="formatPwsId"
                no-wheel
                placeholder="7 digits"
                size="lg"
                class="text-center font-weight-bold"
                @focus="clearPws"
                @keydown.enter="$event.target.blur()"
              ></b-form-input>
              <b-input-group-append v-if="pws_ids.length > 0">
                <b-dropdown
                  variant="outline-secondary"
                  right
                  no-caret
                  title="Recent systems"
                >
                  <template #button-content>
                    <b-icon icon="clock-history"></b-icon>
                  </template>
                  <b-dropdown-item
                    v-for="id in pws_ids"
                    :key="id"
                    @click="pws_id = id"
                  >{{ id }}</b-dropdown-item>
                </b-dropdown>
              </b-input-group-append>
              <b-input-group-append v-if="show_remove_button">
                <b-button
                  variant="outline-secondary"
                  v-b-modal.remove-pws-modal
                  title="Remove from history"
                >
                  <b-icon icon="x"></b-icon>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <div class="text-center mb-3" style="min-height: 1.5rem;">
            <span v-if="pws_name" class="text-success font-weight-bold">{{ pws_name }}</span>
            <span v-else-if="pws_id && pws_id.length == 7 && !searching_pws" class="text-danger">PWS Not Found</span>
            <span v-else class="text-muted">Enter a PWS ID above</span>
          </div>
          <b-button
            block
            @click="fetchSamples"
            variant="primary"
            :disabled="!valid_pws_id"
          >
            <b-spinner small v-if="loading_results" class="mr-1"></b-spinner>
            <b-icon v-else icon="arrow-repeat" class="mr-1"></b-icon>
            {{ sample_results ? 'Reload' : 'Load' }} Results
          </b-button>
        </b-form>
        <b-modal
          id="remove-pws-modal"
          title="Remove from History"
          @ok="onRemovePws"
          ok-variant="danger"
          ok-title="Remove"
          centered
        >
          Remove <strong>{{ pws_id }}</strong> from your history?
        </b-modal>
      </b-card>

      <!-- Subscription Expired Warning -->
      <b-alert
        v-model="showSubscriptionExpired"
        variant="warning"
        dismissible
        class="mx-auto mb-3"
        style="max-width: 28rem;"
      >
        <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon>
        Your notification subscription has expired. Toggle alerts ON to re-subscribe.
      </b-alert>

      <!-- Notifications Card -->
      <b-card
        v-if="notificationsSupported && valid_pws_id"
        class="mx-auto mb-3 shadow-sm"
        style="max-width: 28rem;"
        body-class="p-3"
      >
        <template #header>
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <b-icon icon="bell-fill" class="text-warning mr-2"></b-icon>
              <strong>Notifications</strong>
            </div>
            <b-button
              v-if="subscriptions.length > 0"
              variant="link"
              size="sm"
              class="p-0 text-muted"
              v-b-modal.settings-modal
              title="Manage subscriptions"
            >
              <b-icon icon="gear"></b-icon>
            </b-button>
          </div>
        </template>
        <div class="d-flex align-items-center justify-content-between">
          <b-form-checkbox
            :disabled="buttonDisabled"
            v-model="notificationsButtonOn"
            @change="toggleSubscription"
            name="check-button"
            switch
          >
            <span class="ml-1">Receive alerts</span>
          </b-form-checkbox>
          <b-button
            v-if="currentPwsSubscribed"
            variant="outline-primary"
            size="sm"
            :disabled="testingNotification"
            @click="testNotification"
          >
            <b-spinner small v-if="testingNotification"></b-spinner>
            <span v-else><b-icon icon="bell" class="mr-1"></b-icon>Test</span>
          </b-button>
        </div>
        <b-alert
          v-model="showTestResult"
          :variant="testResultVariant"
          dismissible
          class="mt-2 mb-0 small"
        >
          {{ testResultMessage }}
        </b-alert>
      </b-card>

      <!-- Settings Modal -->
      <b-modal
        id="settings-modal"
        title="Notification Settings"
        ok-only
        ok-variant="dark"
        centered
        @show="onSettingsShow"
      >
        <div v-if="subscriptions.length === 0" class="text-muted text-center py-3">
          No active subscriptions
        </div>
        <div v-else>
          <p class="small text-muted mb-2">Subscribed Systems:</p>
          <b-list-group>
            <b-list-group-item
              v-for="sub in subscriptions"
              :key="sub.pws_id"
              class="d-flex justify-content-between align-items-center py-2"
            >
              <div>
                <strong>{{ sub.pws_id }}</strong>
                <span class="text-muted ml-2 small">{{ sub.pws_name }}</span>
              </div>
              <b-button
                variant="outline-danger"
                size="sm"
                @click="unsubscribePws(sub.pws_id)"
                title="Unsubscribe"
              >
                <b-icon icon="x"></b-icon>
              </b-button>
            </b-list-group-item>
          </b-list-group>
          <p class="small text-muted mt-3 mb-0">
            Toggle "Receive alerts" ON for a PWS to add it here.
          </p>
        </div>

        <!-- Advanced (agency) -->
        <hr class="my-3">
        <div v-if="!agencyUnlocked">
          <b-link class="small text-muted" @click="showAgencyUnlock = !showAgencyUnlock">
            <b-icon :icon="showAgencyUnlock ? 'chevron-down' : 'chevron-right'"></b-icon>
            Advanced
          </b-link>
          <div v-if="showAgencyUnlock" class="mt-2">
            <b-input-group size="sm">
              <b-form-input
                v-model="agencyCodeInput"
                type="password"
                placeholder="Code"
                @keydown.enter="unlockAgency"
              ></b-form-input>
              <b-input-group-append>
                <b-button
                  variant="outline-primary"
                  :disabled="!agencyCodeInput"
                  @click="unlockAgency"
                >Unlock</b-button>
              </b-input-group-append>
            </b-input-group>
            <div v-if="agencyCodeError" class="small text-danger mt-1">{{ agencyCodeError }}</div>
          </div>
        </div>

        <div v-else>
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong class="small">Agency Alerts</strong>
            <b-button variant="link" size="sm" class="p-0 text-muted" @click="lockAgency" title="Lock">
              <b-icon icon="lock"></b-icon>
            </b-button>
          </div>

          <b-form-checkbox v-model="agencyEnabled" switch class="mb-2">
            <span class="ml-1 small">Receive agency alerts (all systems)</span>
          </b-form-checkbox>

          <div v-if="agencyEnabled">
            <div class="small font-weight-bold mt-2">Result Type</div>
            <b-form-checkbox v-model="agencyAlertOnTc" inline>Total Coliform</b-form-checkbox>
            <b-form-checkbox v-model="agencyAlertOnEcoli" inline>E.coli</b-form-checkbox>

            <div class="small font-weight-bold mt-2">Sample Purpose</div>
            <b-form-checkbox-group v-model="agencyPurposes" :options="purposeOptions"></b-form-checkbox-group>

            <div class="small font-weight-bold mt-2">When to Alert</div>
            <b-row class="small mb-1">
              <b-col cols="4" class="pt-1">Weekdays</b-col>
              <b-col cols="4">
                <b-form-input v-model="agencyWeekdayStart" type="time" size="sm"></b-form-input>
              </b-col>
              <b-col cols="4">
                <b-form-input v-model="agencyWeekdayEnd" type="time" size="sm"></b-form-input>
              </b-col>
            </b-row>
            <b-row class="small mb-1">
              <b-col cols="4" class="pt-1">Weekends</b-col>
              <b-col cols="4">
                <b-form-input v-model="agencyWeekendStart" type="time" size="sm"></b-form-input>
              </b-col>
              <b-col cols="4">
                <b-form-input v-model="agencyWeekendEnd" type="time" size="sm"></b-form-input>
              </b-col>
            </b-row>
            <div class="small text-muted">Empty = no alerts. Same start &amp; end = 24 hours. End before start crosses midnight.</div>

            <div class="d-flex mt-3">
              <b-button variant="primary" size="sm" :disabled="agencySaving" @click="saveAgencySettings">
                <b-spinner small v-if="agencySaving"></b-spinner>
                <span v-else>Save</span>
              </b-button>
              <b-button variant="outline-secondary" size="sm" class="ml-2" @click="loadAgencySettings">Reload</b-button>
              <b-button variant="outline-primary" size="sm" class="ml-auto" :disabled="agencyTesting" @click="testAgencyNotification">
                <b-spinner small v-if="agencyTesting"></b-spinner>
                <span v-else><b-icon icon="bell" class="mr-1"></b-icon>Test</span>
              </b-button>
            </div>
            <b-alert v-model="showAgencySaved" variant="success" dismissible class="mt-2 mb-0 small py-1">
              {{ agencySavedMessage }}
            </b-alert>
          </div>
        </div>
      </b-modal>
    </div>
    <!-- Results Card -->
    <b-card
      v-if="pws_id_v"
      class="mx-auto shadow-sm"
      style="max-width: 28rem;"
      body-class="p-2"
      no-body
    >
      <template #header>
        <div class="d-flex align-items-center justify-content-between py-1">
          <div class="d-flex align-items-center">
            <b-icon icon="clipboard-data" class="text-info mr-2"></b-icon>
            <strong>Results</strong>
            <b-badge v-if="sample_results" variant="secondary" class="ml-2">{{ sample_results.length }}</b-badge>
          </div>
          <b-link v-b-modal.modal-legend class="small">Legend</b-link>
        </div>
      </template>
      <b-alert
        class="m-2 mb-0"
        v-model="show_data_error_alert"
        variant="danger"
        dismissible
      >
        Error fetching results
      </b-alert>
      <b-modal id="modal-legend" ok-variant="dark" ok-only centered title="Legend" size="sm">
        <div class="small">
          <div class="mb-2">
            <strong>Results:</strong>
            <span class="ml-2"><b>A</b> = Absent</span>
            <span class="ml-2"><b>P</b> = Present</span>
          </div>
          <div class="mb-2">
            <strong>Chlorine:</strong>
            <span class="ml-2"><b>FCl</b> = Free Chlorine (ppm)</span>
            <span class="ml-2"><b>TCl</b> = Total Chlorine (ppm)</span>
          </div>
          <div class="mb-2">
            <strong>Purpose:</strong>
            <span class="ml-2"><b>RT</b> = Routine</span>
            <span class="ml-2"><b>RP</b> = Repeat</span>
            <span class="ml-2"><b>SP</b> = Special</span>
          </div>
          <div>
            <strong>Row Colors:</strong>
            <div class="bg-warning px-2 py-1 mt-1">Rejected</div>
            <div class="bg-danger text-white px-2 py-1 mt-1">Bacteria Present</div>
          </div>
        </div>
      </b-modal>
      <b-table
        v-if="sample_results"
        small
        striped
        hover
        bordered
        responsive
        head-variant="dark"
        :fields="sample_fields"
        :items="sample_results"
        :tbody-tr-class="rowClass"
        class="mb-0 small"
        thead-class="small"
      >
        <template #cell(sample_point)="row">
          <b-link
            href="#"
            @click.prevent="row.toggleDetails"
            class="font-weight-bold"
          >{{ row.item.sample_point }}</b-link>
        </template>
        <template #row-details="row">
          <div class="bg-light p-2 small text-left">
            <table class="w-100">
              <tr>
                <td class="pr-3 text-nowrap"><strong>Sample ID:</strong> {{ row.item.sample_id }}</td>
                <td class="text-nowrap"><strong>Facility:</strong> {{ row.item.st_asgn_ident_cd }}</td>
              </tr>
              <tr>
                <td class="pr-3"><strong>Collector:</strong> {{ row.item.informal_collector }}</td>
                <td v-if="row.item.ff_chlor_res_msr"><strong>Free Cl:</strong> {{ toFixed(row.item.ff_chlor_res_msr, 2) }} ppm</td>
              </tr>
              <tr>
                <td class="pr-3"><strong>Received:</strong> {{ row.item.date_received }}</td>
                <td v-if="row.item.fldtot_chl_res_msr"><strong>Total Cl:</strong> {{ toFixed(row.item.fldtot_chl_res_msr, 2) }} ppm</td>
              </tr>
            </table>
            <div v-if="row.item.reject_reason_cd && row.item.reject_reason_cd.trim()" class="text-danger mt-1">
              <strong>Reject:</strong> {{ row.item.reject_reason_cd }}
            </div>
            <div v-if="row.item.description_text" class="mt-1">
              <strong>Location:</strong> {{ row.item.description_text }}
            </div>
            <div v-if="row.item.comment_text" class="mt-1">
              <strong>Comments:</strong> {{ row.item.comment_text }}
            </div>
          </div>
        </template>
      </b-table>
      <div v-if="!sample_results && !show_data_error_alert" class="text-center text-muted py-3">
        No results found
      </div>
      <template #footer v-if="sample_results">
        <div class="text-center small text-muted">
          Tap sample point for details
        </div>
      </template>
    </b-card>

    <!-- Footer -->
    <footer class="text-center text-muted small py-4 mt-3">
      © 2020-{{ currentYear }} | MS Sample Notice - Version: {{ appVersion }}
    </footer>
  </div>
</template>

<script>
export default {
  name: "App",
  data() {
    return {
      installBtn: "none",
      showInstallBanner: false,
      canInstall: false,
      isIOS: false,
      installer: undefined,
      notificationsSupported: false,
      notificationsEnabled: false,
      notificationsButtonOn: false,
      buttonDisabled: false,
      serviceWorkerRegistration: null,
      show_data_error_alert: false,
      subscription: null,
      push_message: null,
      pws_id: null,
      pws_ids: [],
      pws_id_v: null,
      pws_name: null,
      sample_fields: [
        { key: "sample_point", sortable: true },
        { key: "test_reason", sortable: true, label: "Purpose" },
        { key: "tc_result", sortable: true, label: "Total" },
        { key: "ecoli_result", sortable: true, label: "Ecoli" },
        { key: "ff_chlor_res_msr", sortable: true, label: "FCl", formatter: (v) => v ? v.toFixed(2) : '-' },
        { key: "fldtot_chl_res_msr", sortable: true, label: "TCl", formatter: (v) => v ? v.toFixed(2) : '-' },
        { key: "dt_collected", sortable: true, label: "DT Collected" }
      ],
      sample_results: null,
      loading_results: false,
      searching_pws: false,
      testingNotification: false,
      showTestResult: false,
      testResultMessage: '',
      testResultVariant: 'success',
      subscriptions: [],
      showSettingsModal: false,
      showSubscriptionExpired: false,
      // Agency
      agencyUnlocked: false,
      agencyCode: "",
      agencyCodeInput: "",
      agencyCodeError: "",
      showAgencyUnlock: false,
      agencyEnabled: false,
      agencyAlertOnTc: true,
      agencyAlertOnEcoli: true,
      agencyPurposes: ["RT", "RP", "SP", "TG"],
      agencyWeekdayStart: "08:00",
      agencyWeekdayEnd: "17:00",
      agencyWeekendStart: "08:00",
      agencyWeekendEnd: "20:00",
      agencySaving: false,
      agencyTesting: false,
      showAgencySaved: false,
      agencySavedMessage: "",
      purposeOptions: [
        { value: "RT", text: "Routine (RT)" },
        { value: "RP", text: "Repeat (RP)" },
        { value: "SP", text: "Special (SP)" },
        { value: "TG", text: "Triggered (TG)" }
      ]
    }
  },
  created() {
    console.log("app created")

    // Detect iOS
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

    // Show install banner for iOS (if not already installed as PWA)
    var isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches
    if (this.isIOS && !isStandalone) {
      this.showInstallBanner = true
    }

    if ("Notification" in window && "serviceWorker" in navigator) {
      this.notificationsSupported = true
    }
    // get public key from server
    this.axios
      .get(`${process.env.VUE_APP_API_PATH}/subscription`)
      .then((res) => {
        // console.log(res)
        localStorage.setItem("applicationServerPublicKey", res.data.public_key)
      })
      .catch((error) => {
        console.error(error)
      })

    // create random ID if we don't have it
    // used to update subscriptions
    if (!localStorage.random_id) {
      localStorage.random_id = this.getRandomString(20)
    }

    // Restore agency unlock state
    if (localStorage.agency_code) {
      this.agencyCode = localStorage.agency_code
      this.agencyUnlocked = true
    }

    let installPrompt

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault()
      installPrompt = e
      this.canInstall = true
      this.showInstallBanner = true
    })

    this.installer = () => {
      this.installBtn = "none"
      installPrompt.prompt()
      installPrompt.userChoice.then((result) => {
        if (result.outcome === "accepted") {
          // console.log('Install accepted!')
        } else {
          // console.log('Install denied!')
        }
      })
    }
  },
  mounted() {
    console.log("app mounted")
    // URL param takes priority over localStorage (deep link from notification)
    const urlPwsId = new URLSearchParams(window.location.search).get("pws_id")
    if (urlPwsId && /^\d{7}$/.test(urlPwsId)) {
      console.log("loaded pws_id from URL")
      this.pws_id = urlPwsId
      // watch on pws_id will fetch name + samples
    } else if (localStorage.pws_id) {
      console.log("loaded pws_id from storage")
      this.pws_id = localStorage.pws_id
      this.pws_id_v = this.pws_id
    }
    if (localStorage.pws_name) {
      console.log("loaded pws_name from storage")
      this.pws_name = localStorage.pws_name
    }
    if (localStorage.pws_ids) {
      this.pws_ids = JSON.parse(localStorage.getItem("pws_ids"))
    }
    // check for existing subscription
    if (navigator.serviceWorker) {
      this.findSubscription().then((sub) => {
        if (sub === null) {
          console.log("no active subscription found on the client", sub)
          this.buttonDisabled = false
          this.notificationsEnabled = false
          this.notificationsButtonOn = false
        } else {
          console.log("Active subscription found", sub)
          this.buttonDisabled = false
          this.subscription = sub
          // Fetch server-side subscriptions to know which PWS are subscribed
          this.fetchSubscriptions()
        }
      })
    }
    // get samples for pws
    if (this.pws_id) {
      this.fetchSamples()
    }
  },
  watch: {
    pws_id: function(newVal) {
      if (newVal.length === 7) {
        this.pws_id_v = ""
        this.fetchPwsName().then(() => {
          if (this.pws_name) {
            console.log("match!!! " + this.pws_name)
            this.pws_id_v = newVal
            this.fetchSamples()
            // Update toggle state for new PWS
            this.notificationsButtonOn = this.currentPwsSubscribed
            this.notificationsEnabled = this.currentPwsSubscribed
          }
        })
      }
    }
  },
  computed: {
    appVersion() {
      return process.env.VUE_APP_VERSION
    },
    currentYear() {
      return new Date().getFullYear()
    },
    currentPwsSubscribed() {
      if (!this.pws_id || this.pws_id.length !== 7) return false
      return this.subscriptions.some(function(s) { return s.pws_id === this.pws_id }.bind(this))
    },
    pws_name_msg: function() {
      if (this.pws_name) {
        return "PWS Name: " + this.pws_name
      } else if (
        this.pws_id &&
        this.pws_id.length == 7 &&
        !this.searching_pws
      ) {
        return "PWS Not Found"
      } else {
        return "---"
      }
    },
    show_remove_button: function() {
      return this.pws_ids.length > 1
    },
    historyOptions: function() {
      return this.pws_ids.map(function(id) {
        return { value: id, text: id }
      })
    },
    valid_pws_id: function() {
      return this.pws_id && (this.pws_id.length == 7)
    }
  },
  methods: {
    toFixed(value, precision) {
      let ret = ''
      if (value) {
        ret = value.toFixed(precision)
      }
      return ret
    },
    getRandomString(length) {
      var randomChars =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
      var result = ""
      for (var i = 0; i < length; i++) {
        result += randomChars.charAt(
          Math.floor(Math.random() * randomChars.length)
        )
      }
      return result
    },
    rowClass(item, type) {
      if (!item || type !== "row") return
      if (item.tc_result === "P") return "table-danger"
      if (item.ecoli_result === "P") return "table-danger"
      if (item.reject_reason_cd.trim()) return "table-warning"
    },
    onRemovePws() {
      this.removePws(this.pws_id)
    },
    onSubmit() {
      console.log("submit")
      return false
    },
    clearPws() {
      this.pws_id = ""
      this.pws_id_v = ""
      this.pws_name = ""
      this.sample_results = null
    },
    formatPwsId(e) {
      return String(e).substring(0, 7)
    },
    fetchSamples() {
      // get samples
      this.show_data_error_alert = false
      this.loading_results = true
      this.axios
        .get(`${process.env.VUE_APP_API_PATH}/getsamples`, {
          params: { pws_id: this.pws_id }
        })
        .then((res) => {
          //console.log(res.data.results)
          this.loading_results = false
          if (res.data.results) {
            this.sample_results = res.data.results
            if (this.sample_results.length == 0) {
              this.sample_results = null
            }
          } else {
            this.sample_results = null
          }
        })
        .catch((error) => {
          this.show_data_error_alert = true
          this.loading_results = false
          console.error(error)
        })
    },
    fetchPwsName() {
      // get pws_name
      this.searching_pws = true
      return this.axios
        .get(`${process.env.VUE_APP_API_PATH}/getpws`, {
          params: { pws_id: this.pws_id }
        })
        .then((res) => {
          // console.log(res.data.pws_name)
          this.searching_pws = false
          if (res.data.pws_name) {
            this.pws_name = res.data.pws_name
            this.addPws(this.pws_id, res.data.pws_name)
          } else {
            this.pws_name = null
          }
        })
        .catch((error) => {
          this.searching_pws = false
          console.error(error)
        })
    },
    addPws(pws_id, pws_name) {
      localStorage.pws_id = pws_id
      localStorage.pws_name = pws_name
      if (this.pws_ids.indexOf(pws_id) == -1) {
        this.pws_ids.push(pws_id)
        localStorage.setItem("pws_ids", JSON.stringify(this.pws_ids))
      }
    },
    removePws(pws_id) {
      const index = this.pws_ids.indexOf(pws_id)
      if (index > -1) {
        this.pws_ids.splice(index, 1)
      }
      localStorage.setItem("pws_ids", JSON.stringify(this.pws_ids))
      this.pws_id = ""
      this.pws_name = ""
    },
    urlBase64ToUint8Array(base64String) {
      const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
      const base64 = (base64String + padding)
        .replace(/-/g, "+")
        .replace(/_/g, "/")
      const rawData = window.atob(base64)
      const outputArray = new Uint8Array(rawData.length)
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
      }
      return outputArray
    },
    toggleSubscription() {
      if (this.notificationsSupported) {
        // Toggle subscription for CURRENT PWS only
        if (!this.currentPwsSubscribed) {
          // Subscribe to current PWS
          console.log("Subscribing to PWS: " + this.pws_id)
          Notification.requestPermission().then((result) => {
            if (result === "granted") {
              this.notificationsButtonOn = false
              this.createSubscription()
                .then((sub) => {
                  console.log("subscription created on the client", sub)
                  this.subscription = sub
                  this.notificationsButtonOn = true
                  // store new subscription on the server for this PWS
                  return this.axios.post(
                    `${process.env.VUE_APP_API_PATH}/subscribe`,
                    {
                      subscription: this.subscription,
                      pws_id: this.pws_id,
                      client_id: localStorage.random_id
                    }
                  )
                })
                .then(() => {
                  this.showNotification()
                  this.buttonDisabled = false
                  this.notificationsEnabled = true
                  this.showSubscriptionExpired = false
                  // Refresh subscriptions list
                  this.fetchSubscriptions()
                })
            } else {
              console.log("User did not grant permission")
              this.notificationsButtonOn = false
            }
          })
        } else {
          // Unsubscribe from current PWS only (keep other subscriptions)
          console.log("Unsubscribing from PWS: " + this.pws_id)
          this.unsubscribePws(this.pws_id)
        }
      }
    },
    createSubscription() {
      console.log("ask for active service worker registration")
      if (this.serviceWorkerRegistration === null) {
        return navigator.serviceWorker.ready // returns a Promise, the active SW registration
          .then((swreg) => {
            this.serviceWorkerRegistration = swreg
            return this.subscribe(this.serviceWorkerRegistration)
          })
      } else {
        return this.subscribe(this.serviceWorkerRegistration)
      }
    },
    getSubscription(swreg) {
      console.log("ask for available subscription")
      return swreg.pushManager.getSubscription()
    },
    subscribe(swreg) {
      console.log("create new subscription for this browser on this device")
      // create new subscription for this browser on this device
      // const vapidPublicKey = process.env.VUE_APP_VAPID_PUBLIC_KEY
      const vapidPublicKey = localStorage.getItem("applicationServerPublicKey")
      console.log("swreg: " + swreg)
      console.log(vapidPublicKey)
      const convertedVapidPublicKey = this.urlBase64ToUint8Array(vapidPublicKey)
      console.log(convertedVapidPublicKey)
      // return the subscription promise, we chain another then where we can send it to the server
      return swreg.pushManager.subscribe({
        userVisibleOnly: true,
        // This is for security. On the backend, we need to do something with the VAPID_PRIVATE_KEY
        // that you can find in .env to make this work in the end
        applicationServerKey: convertedVapidPublicKey
      })
    },
    findSubscription() {
      console.log("get active service worker registration")
      return navigator.serviceWorker.ready.then((swreg) => {
        console.log("haal active subscription op")
        this.serviceWorkerRegistration = swreg
        return this.getSubscription(this.serviceWorkerRegistration)
      })
    },
    showNotification() {
      this.serviceWorkerRegistration.showNotification("You are Subscribed", {
        body: "You will receive notifications for new sample results.",
        icon: "/img/icons/android-chrome-192x192.png",
        // image: '/img/autumn-forest.png',
        vibrate: [300, 200, 300]
        // badge: '/img/icons/plint-badge-96x96.png'
      })
    },
    dosubscribe() {
      console.log("subscribe " + this.subscribe)
      console.log(self)
      if (!self.pushmanager) {
        this.push_message = "push notification not supported."
        return
      }
      if (this.subscribe) {
        this.subscribeuser()
      }
    },
    testNotification() {
      this.testingNotification = true
      this.showTestResult = false
      this.axios
        .post(`${process.env.VUE_APP_API_PATH}/test`, {
          client_id: localStorage.random_id,
          pws_id: this.pws_id
        })
        .then((res) => {
          this.testingNotification = false
          if (res.data.success) {
            this.testResultVariant = 'success'
            this.testResultMessage = 'Test notification sent! You should receive it shortly.'
          } else {
            this.testResultVariant = 'danger'
            this.testResultMessage = 'Failed: ' + (res.data.error || 'Unknown error')
          }
          this.showTestResult = true
        })
        .catch((error) => {
          this.testingNotification = false
          this.testResultVariant = 'danger'
          var errMsg = error.message
          if (error.response && error.response.data && error.response.data.error) {
            errMsg = error.response.data.error
          }
          this.testResultMessage = 'Error: ' + errMsg
          this.showTestResult = true
        })
    },
    fetchSubscriptions() {
      if (!localStorage.random_id) return
      this.axios
        .get(`${process.env.VUE_APP_API_PATH}/subscriptions`, {
          params: { client_id: localStorage.random_id }
        })
        .then((res) => {
          this.subscriptions = res.data.subscriptions || []
          // Update toggle state based on current PWS
          this.notificationsButtonOn = this.currentPwsSubscribed
          this.notificationsEnabled = this.currentPwsSubscribed
          // Show warning if browser has subscription but server has none
          if (this.subscription && this.subscriptions.length === 0) {
            this.showSubscriptionExpired = true
          }
        })
        .catch((error) => {
          console.error('Error fetching subscriptions:', error)
        })
    },
    unsubscribePws(pws_id) {
      this.axios
        .post(`${process.env.VUE_APP_API_PATH}/unsubscribe_pws`, {
          client_id: localStorage.random_id,
          pws_id: pws_id
        })
        .then(() => {
          // Remove from local list
          this.subscriptions = this.subscriptions.filter(function(s) { return s.pws_id !== pws_id })
          // Update toggle if we just unsubscribed from current PWS
          if (pws_id === this.pws_id) {
            this.notificationsButtonOn = false
            this.notificationsEnabled = false
          }
        })
        .catch((error) => {
          console.error('Error unsubscribing:', error)
        })
    },
    onSettingsShow() {
      if (this.agencyUnlocked) {
        this.loadAgencySettings()
      }
    },
    unlockAgency() {
      this.agencyCodeError = ""
      this.axios
        .post(`${process.env.VUE_APP_API_PATH}/agency/redeem`, {
          client_id: localStorage.random_id,
          code: this.agencyCodeInput
        })
        .then((res) => {
          if (res.data.success) {
            this.agencyCode = this.agencyCodeInput
            localStorage.agency_code = this.agencyCode
            this.agencyUnlocked = true
            this.agencyCodeInput = ""
            this.showAgencyUnlock = false
            this.loadAgencySettings()
          } else {
            this.agencyCodeError = res.data.error || "Invalid code"
          }
        })
        .catch((err) => {
          this.agencyCodeError = (err.response && err.response.data && err.response.data.error) || "Invalid code"
        })
    },
    lockAgency() {
      localStorage.removeItem("agency_code")
      this.agencyCode = ""
      this.agencyUnlocked = false
      this.showAgencyUnlock = false
    },
    loadAgencySettings() {
      this.axios
        .get(`${process.env.VUE_APP_API_PATH}/agency/subscription`, {
          params: { client_id: localStorage.random_id, code: this.agencyCode }
        })
        .then((res) => {
          const sub = res.data.subscription
          if (sub) {
            this.agencyEnabled = true
            this.agencyAlertOnTc = sub.alert_on_tc
            this.agencyAlertOnEcoli = sub.alert_on_ecoli
            this.agencyPurposes = sub.purpose_codes
              ? sub.purpose_codes.split(",").map(s => s.trim()).filter(Boolean)
              : ["RT", "RP", "SP", "TG"]
            this.agencyWeekdayStart = sub.weekday_start || ""
            this.agencyWeekdayEnd = sub.weekday_end || ""
            this.agencyWeekendStart = sub.weekend_start || ""
            this.agencyWeekendEnd = sub.weekend_end || ""
          } else {
            this.agencyEnabled = false
          }
        })
        .catch((err) => {
          console.error("agency settings load failed", err)
        })
    },
    testAgencyNotification() {
      this.agencyTesting = true
      this.showAgencySaved = false
      this.axios
        .post(`${process.env.VUE_APP_API_PATH}/agency/test`, {
          client_id: localStorage.random_id,
          code: this.agencyCode
        })
        .then((res) => {
          this.agencyTesting = false
          if (res.data.success) {
            this.agencySavedMessage = "Test sent — should arrive shortly"
          } else {
            this.agencySavedMessage = "Test failed: " + (res.data.error || "unknown")
          }
          this.showAgencySaved = true
        })
        .catch((err) => {
          this.agencyTesting = false
          const msg = (err.response && err.response.data && err.response.data.error) || err.message
          this.agencySavedMessage = "Test failed: " + msg
          this.showAgencySaved = true
        })
    },
    saveAgencySettings() {
      this.agencySaving = true
      this.showAgencySaved = false
      const finish = (msg, ok) => {
        this.agencySaving = false
        this.agencySavedMessage = msg
        this.showAgencySaved = true
        if (!ok) console.error(msg)
      }
      if (!this.agencyEnabled) {
        this.axios
          .post(`${process.env.VUE_APP_API_PATH}/agency/unsubscribe`, {
            client_id: localStorage.random_id,
            code: this.agencyCode
          })
          .then(() => finish("Agency alerts disabled", true))
          .catch(() => finish("Failed to disable", false))
        return
      }
      // Ensure permission + push subscription
      Notification.requestPermission().then((result) => {
        if (result !== "granted") {
          finish("Notification permission denied", false)
          return
        }
        this.createSubscription()
          .then((pushSub) => {
            this.subscription = pushSub
            return this.axios.post(`${process.env.VUE_APP_API_PATH}/agency/subscribe`, {
              client_id: localStorage.random_id,
              code: this.agencyCode,
              subscription: pushSub,
              purpose_codes: this.agencyPurposes.join(","),
              alert_on_tc: this.agencyAlertOnTc,
              alert_on_ecoli: this.agencyAlertOnEcoli,
              weekday_start: this.agencyWeekdayStart || null,
              weekday_end: this.agencyWeekdayEnd || null,
              weekend_start: this.agencyWeekendStart || null,
              weekend_end: this.agencyWeekendEnd || null
            })
          })
          .then(() => finish("Agency alerts saved", true))
          .catch((err) => {
            const msg = (err.response && err.response.data && err.response.data.error) || "Save failed"
            finish(msg, false)
          })
      })
    }
  }
}
</script>

<style>
#app {
  /*font-family: Avenir, Helvetica, Arial, sans-serif;*/
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  /*color: #2c3e50;*/
}
#app .navbar-nav {
  text-align: left;
}
</style>
